/**
 * SickLeavePage.js
 *
 * Main page for Sick Leave Management.
 * Month-scoped calendar — displays sick leave for the selected month,
 * but subscribes to the full year so summary stats remain accurate.
 * No HolidayProvider needed (sick leave has no holiday integration).
 *
 * Mirrors: TeamVacationPage.js
 * Differences:
 * - Month + year navigation instead of month-only
 * - SickLeaveDayPanel is managed here, not inside the calendar
 * - No HolidayProvider wrapper
 * - SickLeaveSummaryStats only rendered when a specific staff member is selected
 *
 * SL-V2-1a: Replaced year-only navigation with month + year navigation.
 * - selectedMonth state added (0–11)
 * - handlePreviousMonth / handleNextMonth replace handlePreviousYear / handleNextYear
 * - Navigation label now shows formatted "Month Year" string via i18n locale
 * - SickLeaveCalendar receives month and staff props
 * - SickLeaveSummaryStats receives month and selectedStaffId props
 * - Year subscription remains unchanged — full year loaded, month filtered client-side
 *
 * ARCHIVE: staffMembers now sourced from useStaff().activeStaff (aliased) so
 * archived employees are excluded from department/staff filters and the
 * calendar/summary/modal/panel that consume filteredStaff downstream.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useSickLeave } from '../../contexts/SickLeaveContext';
import { useDepartment } from '../../contexts/DepartmentContext';
import { useStaff } from '../../contexts/StaffContext';
import SickLeaveCalendar from '../../components/sickleave/SickLeaveCalendar';
import SickLeaveAddModal from '../../components/sickleave/SickLeaveAddModal';
import SickLeaveSummaryStats from '../../components/sickleave/SickLeaveSummaryStats';
import SickLeaveDayPanel from '../../components/sickleave/SickLeaveDayPanel';
import '../../styles/vacation.css';
import '../../styles/sick-leave.css';

const SickLeavePage = () => {
  const { t, i18n } = useTranslation('sickLeave');
  const { currentUser, userRole } = useAuth();
  const { departments, loading: departmentsLoading } = useDepartment();
  const { activeStaff: staffMembers, loading: staffLoading } = useStaff();
  const {
    sickLeave,
    loading: sickLeaveLoading,
    subscribeToSickLeave,
    subscribeToSickLeaveByStaffIds,
    unsubscribe
  } = useSickLeave();

  const currentDate = new Date();
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth()); // 0–11
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  const [selectedDepartmentId, setSelectedDepartmentId] = useState('all');
  const [selectedStaffId, setSelectedStaffId] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  /**
   * Get accessible departments based on user role.
   * Owner: all departments. Manager: assigned departments only.
   */
  const accessibleDepartments = useMemo(() => {
    if (userRole === 'owner') {
      return departments;
    }
    if (userRole === 'manager') {
      return departments.filter(dept =>
        currentUser?.departmentIds?.includes(dept.id)
      );
    }
    return [];
  }, [departments, userRole, currentUser]);

  /**
   * Filter staff to only those belonging to accessible (or selected) departments.
   */
  const filteredStaff = useMemo(() => {
    if (selectedDepartmentId === 'all') {
      const accessibleDeptIds = accessibleDepartments.map(d => d.id);
      return staffMembers.filter(s =>
        s.departmentIds?.some(deptId => accessibleDeptIds.includes(deptId))
      );
    }
    return staffMembers.filter(s =>
      s.departmentIds?.includes(selectedDepartmentId)
    );
  }, [selectedDepartmentId, staffMembers, accessibleDepartments]);

  /**
   * Subscribe to sick leave for the full selected year.
   *
   * The calendar displays one month at a time and filters the year data client-side.
   * The year subscription is kept intact so summary stats remain accurate for
   * the full year regardless of which month is currently displayed.
   *
   * When a specific staff member is selected, query by that staffId only.
   * When a department is selected, query by the staffIds of members in that
   * department — not by departmentId on the record — so that sick leave
   * recorded against any department is captured correctly.
   * When owner views all departments, subscribe without scope restriction.
   * When manager views all their departments, query by their staff membership.
   *
   * selectedMonth is intentionally excluded from the dependency array.
   * Re-subscribes only when year, department, staff filter, or role changes.
   */
  useEffect(() => {
    if (departmentsLoading || staffLoading) return;

    const startDate = new Date(selectedYear, 0, 1);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(selectedYear, 11, 31);
    endDate.setHours(23, 59, 59, 999);

    let unsubscribeFn;

    if (selectedStaffId !== 'all') {
      // Single staff member selected — subscribe for that one staff ID only
      unsubscribeFn = subscribeToSickLeaveByStaffIds(
        startDate,
        endDate,
        [selectedStaffId]
      );
    } else if (selectedDepartmentId !== 'all') {
      // Department selected — query by staff membership, not by departmentId field
      const staffIds = filteredStaff.map(s => s.id);
      unsubscribeFn = subscribeToSickLeaveByStaffIds(
        startDate,
        endDate,
        staffIds
      );
    } else if (userRole === 'owner') {
      // All departments, all staff — no scope restriction
      unsubscribeFn = subscribeToSickLeave(startDate, endDate, null);
    } else if (userRole === 'manager' && accessibleDepartments.length > 0) {
      // Manager viewing all their departments — scope to their staff
      const staffIds = filteredStaff.map(s => s.id);
      unsubscribeFn = subscribeToSickLeaveByStaffIds(
        startDate,
        endDate,
        staffIds
      );
    }

    return () => {
      if (unsubscribeFn) unsubscribeFn();
    };
  }, [
    selectedYear,
    selectedDepartmentId,
    selectedStaffId,
    filteredStaff,
    userRole,
    accessibleDepartments,
    departmentsLoading,
    staffLoading,
    subscribeToSickLeave,
    subscribeToSickLeaveByStaffIds
  ]);

  /**
   * Cleanup listener on unmount.
   */
  useEffect(() => {
    return () => { unsubscribe(); };
  }, [unsubscribe]);

  /**
   * Month navigation handlers.
   * Rolling over the year boundary increments or decrements selectedYear,
   * which triggers a new year subscription automatically via the useEffect above.
   */
  const handlePreviousMonth = useCallback(() => {
    setSelectedMonth(prev => {
      if (prev === 0) {
        setSelectedYear(y => y - 1);
        return 11;
      }
      return prev - 1;
    });
  }, []);

  const handleNextMonth = useCallback(() => {
    setSelectedMonth(prev => {
      if (prev === 11) {
        setSelectedYear(y => y + 1);
        return 0;
      }
      return prev + 1;
    });
  }, []);

  /**
   * Formatted navigation label — e.g. "January 2025" / "Januar 2025".
   * Generated from the active i18n locale so it localises automatically.
   */
  const monthLabel = useMemo(() => {
    return new Date(selectedYear, selectedMonth, 1).toLocaleDateString(
      i18n.language,
      { month: 'long', year: 'numeric' }
    );
  }, [selectedYear, selectedMonth, i18n.language]);

  /**
   * Department filter — also resets the staff selection so the staff
   * dropdown repopulates with the new department's members.
   */
  const handleDepartmentChange = useCallback((e) => {
    setSelectedDepartmentId(e.target.value);
    setSelectedStaffId('all');
  }, []);

  /**
   * Staff member filter.
   */
  const handleStaffChange = useCallback((e) => {
    setSelectedStaffId(e.target.value);
  }, []);

  /**
   * Calendar day click — dateString is always YYYY-MM-DD.
   * Parse as local date to avoid timezone shift.
   */
  const handleDayClick = useCallback((dateString) => {
    const [year, month, day] = dateString.split('-').map(Number);
    setSelectedDate(new Date(year, month - 1, day));
    setIsPanelOpen(true);
  }, []);

  const handlePanelClose = useCallback(() => {
    setIsPanelOpen(false);
    setSelectedDate(null);
  }, []);

  const handleOpenAddModal = useCallback(() => {
    setShowAddModal(true);
  }, []);

  const handleCloseAddModal = useCallback(() => {
    setShowAddModal(false);
  }, []);

  /**
   * Derive the sick leave entries for the currently selected date.
   * Used to populate SickLeaveDayPanel.
   */
  const sickLeaveForSelectedDate = useMemo(() => {
    if (!selectedDate) return [];
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedDate.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    return sickLeave.filter(s => s.getDateString() === dateStr);
  }, [selectedDate, sickLeave]);

  const isLoading = departmentsLoading || staffLoading || sickLeaveLoading;

  return (
    <div className="team-vacation-page">

      {/* Page Header */}
      <div className="team-vacation-header">
        <div className="header-content">
          <h1>{t('title')}</h1>
        </div>
        <button
          className="btn btn-primary"
          onClick={handleOpenAddModal}
          disabled={isLoading}
        >
          + {t('addSickDay')}
        </button>
      </div>

      {/* Filters and Month Navigation */}
      <div className="vacation-filters">

        {/* Department Filter */}
        <div className="filter-group">
          <label htmlFor="sl-department-filter">{t('department')}</label>
          <select
            id="sl-department-filter"
            value={selectedDepartmentId}
            onChange={handleDepartmentChange}
            disabled={isLoading || accessibleDepartments.length === 0}
          >
            <option value="all">
              {t('allDepartments', { ns: 'vacation', defaultValue: 'All Departments' })}
            </option>
            {accessibleDepartments.map(dept => (
              <option key={dept.id} value={dept.id}>
                {dept.name}
              </option>
            ))}
          </select>
        </div>

        {/* Staff Filter */}
        <div className="filter-group">
          <label htmlFor="sl-staff-filter">{t('staffMember')}</label>
          <select
            id="sl-staff-filter"
            value={selectedStaffId}
            onChange={handleStaffChange}
            disabled={isLoading || filteredStaff.length === 0}
          >
            <option value="all">
              {t('allStaff', { ns: 'vacation', defaultValue: 'All Staff' })}
            </option>
            {filteredStaff.map(member => (
              <option key={member.id} value={member.id}>
                {member.name}
              </option>
            ))}
          </select>
        </div>

        {/* Month Navigation */}
        <div className="filter-group month-navigation-inline">
          <label>&nbsp;</label>
          <div className="month-navigation-controls">
            <button
              className="btn btn-icon"
              onClick={handlePreviousMonth}
              disabled={isLoading}
              aria-label={t('previousMonth', { defaultValue: 'Previous Month' })}
              title={t('previousMonth', { defaultValue: 'Previous Month' })}
            >
              ←
            </button>
            <span className="month-display">{monthLabel}</span>
            <button
              className="btn btn-icon"
              onClick={handleNextMonth}
              disabled={isLoading}
              aria-label={t('nextMonth', { defaultValue: 'Next Month' })}
              title={t('nextMonth', { defaultValue: 'Next Month' })}
            >
              →
            </button>
          </div>
        </div>

      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="loading-overlay">
          <div className="spinner"></div>
          <p>{t('loading', { defaultValue: 'Loading...' })}</p>
        </div>
      )}

      {/* No Department Access */}
      {!isLoading && accessibleDepartments.length === 0 && (
        <div className="no-access-message">
          <p>{t('messages.noAccessDepartments', { ns: 'vacation', defaultValue: 'No departments available.' })}</p>
          <p>{t('messages.contactAdministrator', { ns: 'vacation', defaultValue: 'Contact your administrator.' })}</p>
        </div>
      )}

      {/* Month Calendar */}
      {!isLoading && accessibleDepartments.length > 0 && (
        <SickLeaveCalendar
          year={selectedYear}
          month={selectedMonth}
          sickLeave={sickLeave}
          staff={filteredStaff}
          onDayClick={handleDayClick}
        />
      )}

      {/* Summary Stats — only visible when a specific staff member is selected */}
      {!isLoading && accessibleDepartments.length > 0 && selectedStaffId !== 'all' && (
        <SickLeaveSummaryStats
          year={selectedYear}
          month={selectedMonth}
          sickLeave={sickLeave}
          staff={filteredStaff}
          selectedStaffId={selectedStaffId}
        />
      )}

      {/* Add Sick Day Modal */}
      {showAddModal && (
        <SickLeaveAddModal
          isOpen={showAddModal}
          onClose={handleCloseAddModal}
          departments={accessibleDepartments}
          staff={filteredStaff}
        />
      )}

      {/* Day Panel — only mounted when a date is selected */}
      {selectedDate && (
        <SickLeaveDayPanel
          isOpen={isPanelOpen}
          onClose={handlePanelClose}
          date={selectedDate}
          sickLeave={sickLeaveForSelectedDate}
          staff={filteredStaff}
          departments={accessibleDepartments}
        />
      )}

    </div>
  );
};

export default SickLeavePage;